import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Users, Plus, Pencil, Trash2, Loader2, Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";
import {
  useArtists,
  useCreateArtist,
  useUpdateArtist,
  useDeleteArtist,
  type Artist,
} from "@api/artists";
import { ArtistForm } from "@components/ArtistForm";
import { t } from "@lib/i18n";

const PAGE_SIZE = 20;

export function Artists() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingArtist, setEditingArtist] = useState<Artist | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  const { data: artistsData, isLoading, error } = useArtists({
    page,
    page_size: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };
  
  const createMutation = useCreateArtist();
  const updateMutation = useUpdateArtist();
  const deleteMutation = useDeleteArtist();

  const handleCreate = (data: Parameters<typeof createMutation.mutate>[0]) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setIsCreating(false);
        // Reset to first page to see the new artist
        setPage(1);
      },
    });
  };

  const handleUpdate = (data: Parameters<typeof updateMutation.mutate>[0]["data"]) => {
    if (editingArtist) {
      updateMutation.mutate(
        { id: editingArtist.id, data },
        {
          onSuccess: () => {
            setEditingArtist(null);
          },
        }
      );
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(t("artists.deleteConfirm"))) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{t("artists.title")}</h1>
            <p className="text-sm text-muted-foreground">
              {t("artists.description")}
            </p>
          </div>
        </div>
        <Button onClick={() => setIsCreating(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          {t("artists.createArtist")}
        </Button>
      </div>

      {/* Create/Edit Form */}
      {(isCreating || editingArtist) && (
        <Card>
          <CardHeader>
            <CardTitle>{editingArtist ? t("artists.editArtist") : t("artists.createArtist")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ArtistForm
              initialData={editingArtist || undefined}
              onSubmit={editingArtist ? handleUpdate : handleCreate}
              onCancel={() => {
                setIsCreating(false);
                setEditingArtist(null);
              }}
              isLoading={createMutation.isPending || updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {/* Artists List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t("artists.artistsList")}</CardTitle>
            {artistsData && (
              <div className="text-sm text-muted-foreground">
                {t("common.showing")} {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, artistsData.total)} {t("common.of")} {artistsData.total}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("common.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive py-4">
              {t("artists.errorLoading")}: {error instanceof Error ? error.message : t("common.unknownError")}
            </div>
          )}

          {!isLoading && !error && artistsData && artistsData.list.length === 0 && (
            <p className="text-muted-foreground py-4">{t("artists.noArtists")}</p>
          )}

          {!isLoading && !error && artistsData && artistsData.list.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("id")}
                      >
                        {t("common.id")}
                        <SortIcon column="id" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("name")}
                      >
                        {t("common.name")}
                        <SortIcon column="name" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("slug")}
                      >
                        {t("common.slug")}
                        <SortIcon column="slug" />
                      </th>
                      <th className="text-right p-2 font-medium">{t("common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artistsData.list.map((artist) => (
                      <tr key={artist.id} className="border-b hover:bg-muted/50">
                        <td className="p-2">{artist.id}</td>
                        <td className="p-2 font-medium">{artist.name}</td>
                        <td className="p-2 text-muted-foreground">{artist.slug}</td>
                        <td className="p-2">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingArtist(artist)}
                              className="gap-1"
                            >
                              <Pencil className="w-4 h-4" />
                              {t("common.edit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(artist.id)}
                              disabled={deleteMutation.isPending}
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t("common.delete")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {artistsData.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t("common.page")} {artistsData.page} {t("common.of")} {artistsData.total_pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1 || isLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t("common.previous")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(artistsData.total_pages, p + 1))}
                      disabled={page === artistsData.total_pages || isLoading}
                      className="gap-1"
                    >
                      {t("common.next")}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
